import React, { useState } from 'react';
import type { Theme } from '@mui/material';
import { Rating, Box, Typography, styled, useTheme } from '@mui/material';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import type { StarRatingProps, StarRatingSize, StarRatingColor } from './types';

// Size configurations for consistent scaling
const sizeConfig: Record<StarRatingSize, { fontSize: string; valueSize: string; gap: number }> = {
  small: {
    fontSize: '1rem',
    valueSize: '0.75rem',
    gap: 0.5,
  },
  medium: {
    fontSize: '1.5rem',
    valueSize: '0.875rem',
    gap: 0.75,
  },
  large: {
    fontSize: '2rem',
    valueSize: '1rem',
    gap: 1,
  },
};

// MUI palette colors that we can reference from theme
const paletteColors = ['warning', 'primary', 'secondary', 'error', 'info', 'success'] as const;

/**
 * Resolves color to a CSS value, supporting both MUI palette colors and custom colors
 */
const resolveColor = (
  color: StarRatingColor,
  theme: Theme,
  variant: 'main' | 'light' = 'main',
): string => {
  if (paletteColors.includes(color as (typeof paletteColors)[number])) {
    return theme.palette[color as (typeof paletteColors)[number]][variant];
  }
  // For custom colors, return as-is for main, lighten for hover
  if (variant === 'light') {
    return color; // Custom colors don't auto-lighten; could enhance with color manipulation
  }
  return color;
};

// Styled Rating component with custom theming and animations
const StyledRating = styled(Rating, {
  shouldForwardProp: (prop) => !['customSize', 'starColor', 'hoverColor'].includes(prop as string),
})<{ customSize: StarRatingSize; starColor: string; hoverColor: string }>(
  ({ theme, customSize, starColor, hoverColor }) => ({
    '& .MuiRating-icon': {
      fontSize: sizeConfig[customSize].fontSize,
      transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.2s ease',
    },
    '& .MuiRating-iconFilled': {
      color: starColor,
    },
    '& .MuiRating-iconHover': {
      color: hoverColor,
    },
    '& .MuiRating-iconEmpty': {
      color: theme.palette.action.disabled,
      transition: 'color 0.2s ease, transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    // Interactive styles
    '&:not(.Mui-readOnly):not(.Mui-disabled)': {
      '& .MuiRating-icon': {
        cursor: 'pointer',
      },
      '& .MuiRating-iconActive': {
        transform: 'scale(1.2)',
      },
      '& .MuiRating-icon:hover': {
        transform: 'scale(1.15)',
      },
    },
    // Disabled state with transition
    '&.Mui-disabled': {
      opacity: 0.5,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease',
    },
    // Label container transitions
    '& .MuiRating-label': {
      transition: 'transform 0.15s ease',
    },
  }),
);

const ValueDisplay = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'customSize',
})<{ customSize: StarRatingSize }>(({ customSize }) => ({
  fontSize: sizeConfig[customSize].valueSize,
  fontWeight: 600,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  transition: 'color 0.2s ease, transform 0.15s ease',
}));

/**
 * StarRating component for displaying or collecting star ratings.
 *
 * @example
 * // Read-only display with value
 * <StarRating value={4.5} readOnly showValue precision={0.5} />
 *
 * @example
 * // Interactive rating input
 * <StarRating
 *   value={rating}
 *   onChange={(e, newValue) => setRating(newValue)}
 * />
 *
 * @example
 * // Custom color
 * <StarRating value={4} color="primary" readOnly />
 * <StarRating value={3} color="#ff6b6b" readOnly />
 */
export const StarRating: React.FC<StarRatingProps> = ({
  value,
  max = 5,
  size = 'medium',
  color = 'warning',
  readOnly = false,
  disabled = false,
  showValue = false,
  precision = 1,
  onChange,
  onHoverChange,
  emptyIcon,
  filledIcon,
  ariaLabel,
  sx,
  className,
}) => {
  const theme = useTheme();
  const [hoverValue, setHoverValue] = useState<number>(-1);

  // Resolve colors from theme or use custom values
  const starColor = resolveColor(color, theme, 'main');
  const hoverColor = resolveColor(color, theme, 'light');

  const handleChange = (event: React.SyntheticEvent, newValue: number | null) => {
    if (!readOnly && !disabled && onChange) {
      onChange(event, newValue);
    }
  };

  const handleHoverChange = (event: React.SyntheticEvent, newHoverValue: number) => {
    setHoverValue(newHoverValue);
    if (!readOnly && !disabled && onHoverChange) {
      onHoverChange(event, newHoverValue);
    }
  };

  // Determine which value to display (hover value takes precedence when hovering)
  const displayValue = hoverValue !== -1 ? hoverValue : value;

  // Format the value for display
  const formattedValue =
    displayValue !== null
      ? Number.isInteger(displayValue)
        ? displayValue.toString()
        : displayValue.toFixed(1)
      : '—';

  // Default icons with inherited font size for proper scaling
  const defaultEmptyIcon = <StarBorder fontSize="inherit" />;
  const defaultFilledIcon = <Star fontSize="inherit" />;

  return (
    <Box
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sizeConfig[size].gap,
        transition: 'opacity 0.2s ease',
        ...sx,
      }}
    >
      <StyledRating
        customSize={size}
        starColor={starColor}
        hoverColor={hoverColor}
        value={value}
        max={max}
        readOnly={readOnly}
        disabled={disabled}
        precision={precision}
        onChange={handleChange}
        onChangeActive={handleHoverChange}
        emptyIcon={emptyIcon ?? defaultEmptyIcon}
        icon={filledIcon ?? defaultFilledIcon}
        aria-label={ariaLabel ?? `Rating: ${formattedValue} out of ${max} stars`}
      />
      {showValue && (
        <ValueDisplay
          customSize={size}
          sx={{
            color: hoverValue !== -1 ? starColor : 'text.primary',
          }}
        >
          {formattedValue}
        </ValueDisplay>
      )}
    </Box>
  );
};

export default StarRating;
